import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Lock, TrendingUp, TrendingDown, Settings, Plus, Download,
  CreditCard, BanknoteIcon, FileText, Zap, Trash2, AlertTriangle,
  Calendar as CalIcon, RefreshCw, ExternalLink
} from 'lucide-react';
import FinanceCharts from '../components/finance/FinanceCharts';
import FinanceConfigDialog from '../components/finance/FinanceConfigDialog';
import FinanceInvoiceDialog from '../components/finance/FinanceInvoiceDialog';
import FinancePaymentDialog from '../components/finance/FinancePaymentDialog';
import FinanceExpenseDialog from '../components/finance/FinanceExpenseDialog';
import FinanceCRMWebhookDialog from '../components/finance/FinanceCRMWebhookDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

// Default period: current month
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthsAgoKey = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function Finance() {
  const { user } = useAuth();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  const [start, setStart] = useState(monthsAgoKey(5));
  const [end, setEnd] = useState(todayKey());

  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState({ months: [], series: [] });
  const [clientsConfig, setClientsConfig] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [configDialog, setConfigDialog] = useState(null); // client row
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [webhookDialog, setWebhookDialog] = useState(false);

  // 1) Check role
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/finance/role-check`)
      .then(({ data }) => {
        if (!alive) return;
        setHasAccess(!!data.has_access);
        setAccessChecked(true);
      })
      .catch(() => { if (alive) { setHasAccess(false); setAccessChecked(true); } });
    return () => { alive = false; };
  }, []);

  // 2) Load data when access granted + period changes
  const loadAll = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      const [s, ts, cfg, inv, pay] = await Promise.all([
        axios.get(`${API}/finance/summary?start=${start}&end=${end}`),
        axios.get(`${API}/finance/timeseries?start=${start}&end=${end}`),
        axios.get(`${API}/finance/clients-config`),
        axios.get(`${API}/finance/invoices?start=${start}&end=${end}`),
        axios.get(`${API}/finance/payments?start=${start}-01&end=${end}-31`),
      ]);
      setSummary(s.data);
      setTimeseries(ts.data);
      setClientsConfig(cfg.data);
      setInvoices(inv.data);
      setPayments(pay.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [hasAccess, start, end]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleGenerateRecurring = async () => {
    if (!window.confirm(`Générer les factures récurrentes (forfait + backlinks) pour ${end} ? Les factures existantes ne seront pas dupliquées.`)) return;
    try {
      const { data } = await axios.post(`${API}/finance/invoices/generate-recurring`, { month: end });
      toast.success(`${data.count} facture(s) créée(s) · ${data.skipped.length} ignorée(s)`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération");
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Supprimer cette facture ? Les paiements liés seront aussi supprimés.")) return;
    try {
      await axios.delete(`${API}/finance/invoices/${id}`);
      toast.success("Facture supprimée");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm("Supprimer ce paiement ?")) return;
    try {
      await axios.delete(`${API}/finance/payments/${id}`);
      toast.success("Paiement supprimé");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleSendToCRM = async (invoiceId, target) => {
    try {
      const { data } = await axios.post(`${API}/finance/invoices/${invoiceId}/send-to-crm`, { target });
      toast.success(`Envoyé vers ${target} · ref ${data.result?.external_ref}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Échec envoi ${target}`);
    }
  };

  const exportCSV = () => {
    if (!summary) return;
    const headers = ["Client","Forfait","Backlinks fact.","Site","Autres","Total fact.","Encaissé","Arriéré","Coût consultant","Coût backlinks","Marge"];
    const lines = [headers.join(";")];
    summary.rows.forEach(r => {
      lines.push([r.name, r.billed_monthly, r.billed_backlinks, r.billed_website, r.billed_other, r.total_billed, r.total_paid, r.outstanding, r.consultant_cost, r.backlinks_cost, r.margin].join(";"));
    });
    lines.push([
      "TOTAL", summary.totals.billed_monthly, summary.totals.billed_backlinks,
      summary.totals.billed_website, summary.totals.billed_other,
      summary.totals.total_billed, summary.totals.total_paid, summary.totals.outstanding,
      summary.totals.consultant_cost, summary.totals.backlinks_cost, summary.totals.margin
    ].join(";"));
    lines.push(`Frais généraux;${summary.expenses.overhead}`);
    lines.push(`Frais marketing;${summary.expenses.marketing}`);
    lines.push(`Marge nette entreprise;${summary.net_margin}`);
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `finance-${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // --- Loading / locked states ---
  if (!accessChecked) {
    return <Layout><div className="p-12 text-center text-neutral-500">Vérification de l'accès…</div></Layout>;
  }
  if (!hasAccess) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-12" data-testid="finance-locked">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-700" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-neutral-900 mb-2">Module Résultats — Accès réservé</h1>
            <p className="text-neutral-600 leading-relaxed">
              Ce tableau de bord financier est strictement réservé au rôle <strong>Admin Finance</strong>.
              Il regroupe la facturation, les rentrées bancaires, les coûts internes et la rentabilité de
              l'entreprise — des informations confidentielles non visibles dans les autres parties du SaaS.
            </p>
            <p className="text-sm text-neutral-500 mt-4">
              Votre rôle actuel : <code className="bg-white px-2 py-0.5 rounded border border-neutral-200">{user?.role || 'inconnu'}</code>
            </p>
            <p className="text-sm text-amber-800 mt-6 italic">
              💡 Pour obtenir l'accès, un administrateur doit vous attribuer le rôle <strong>admin_finance</strong> dans Utilisateurs.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const netMarginColor = (summary?.net_margin || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600';

  return (
    <Layout>
      <div className="space-y-6" data-testid="finance-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Résultats — Rentabilité entreprise</h1>
              <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                <Lock className="w-3 h-3" /> FINANCE
              </span>
            </div>
            <p className="text-neutral-500">Vue d'ensemble facturation, encaissements, coûts et marge — données isolées de l'interface client</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label htmlFor="start" className="text-xs text-neutral-500">Du mois</Label>
              <Input id="start" type="month" value={start} onChange={(e) => setStart(e.target.value)} className="w-[150px]" data-testid="finance-start" />
            </div>
            <div>
              <Label htmlFor="end" className="text-xs text-neutral-500">Au mois</Label>
              <Input id="end" type="month" value={end} onChange={(e) => setEnd(e.target.value)} className="w-[150px]" data-testid="finance-end" />
            </div>
            <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="finance-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
            <Button variant="outline" onClick={exportCSV} data-testid="finance-export-csv">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="finance-kpis">
            <KPI icon={FileText} label="Facturation période" value={summary.totals.total_billed} cls="bg-blue-50 text-blue-700 border-blue-200" testid="kpi-billed" />
            <KPI icon={BanknoteIcon} label="Encaissé banque" value={summary.totals.total_paid} cls="bg-emerald-50 text-emerald-700 border-emerald-200" testid="kpi-collected" />
            <KPI icon={AlertTriangle} label="Arriérés" value={summary.totals.outstanding} cls="bg-amber-50 text-amber-700 border-amber-200" testid="kpi-outstanding" />
            <KPI icon={CreditCard} label="Coûts internes" value={summary.totals.consultant_cost + summary.totals.backlinks_cost + summary.expenses.total} cls="bg-rose-50 text-rose-700 border-rose-200" testid="kpi-costs" />
            <KPI icon={summary.net_margin >= 0 ? TrendingUp : TrendingDown} label="Marge nette entreprise" value={summary.net_margin} cls={summary.net_margin >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"} highlight testid="kpi-net-margin" />
          </div>
        )}

        {/* Action bar */}
        <div className="bg-white border border-neutral-200 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="finance-actions">
          <Button size="sm" onClick={() => setInvoiceDialog({ new: true })} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-new-invoice">
            <Plus className="w-4 h-4 mr-1" /> Facture
          </Button>
          <Button size="sm" onClick={() => setPaymentDialog({ new: true })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-new-payment">
            <Plus className="w-4 h-4 mr-1" /> Encaissement
          </Button>
          <Button size="sm" variant="outline" onClick={() => setExpenseDialog(true)} data-testid="btn-new-expense">
            <Plus className="w-4 h-4 mr-1" /> Frais (généraux / marketing)
          </Button>
          <Button size="sm" variant="outline" onClick={handleGenerateRecurring} data-testid="btn-generate-recurring">
            <Zap className="w-4 h-4 mr-1" /> Générer factures récurrentes du mois ({end})
          </Button>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setWebhookDialog(true)} data-testid="btn-crm-webhook">
              <Settings className="w-4 h-4 mr-1" /> Webhook CRM custom
            </Button>
          </div>
        </div>

        {/* Main Synthèse table */}
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden" data-testid="finance-summary-table">
          <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-lg text-neutral-900">Synthèse par client — {start} → {end}</h2>
              <p className="text-xs text-neutral-500">Clients marqués payants uniquement · {summary?.months_count || 0} mois · marge = facturation − coût consultant − coût backlinks</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-right px-3 py-2">Forfait</th>
                  <th className="text-right px-3 py-2">Backlinks fact.</th>
                  <th className="text-right px-3 py-2">Site</th>
                  <th className="text-right px-3 py-2">Autres</th>
                  <th className="text-right px-3 py-2 bg-blue-50 text-blue-700">Total facturé</th>
                  <th className="text-right px-3 py-2 bg-emerald-50 text-emerald-700">Encaissé</th>
                  <th className="text-right px-3 py-2 bg-amber-50 text-amber-700">Arriéré</th>
                  <th className="text-right px-3 py-2">Coût consultant</th>
                  <th className="text-right px-3 py-2">Coût backlinks</th>
                  <th className="text-right px-3 py-2 bg-neutral-100 text-neutral-900 font-semibold">Marge</th>
                </tr>
              </thead>
              <tbody>
                {summary?.rows?.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-neutral-400 py-8">
                      Aucun client payant configuré.{' '}
                      <button className="text-blue-600 underline" onClick={() => {
                        const firstClient = clientsConfig[0];
                        if (firstClient) setConfigDialog(firstClient);
                      }}>Configurer un client →</button>
                    </td>
                  </tr>
                )}
                {summary?.rows?.map(r => (
                  <tr key={r.client_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`finance-row-${r.client_id}`}>
                    <td className="px-3 py-2 font-medium text-neutral-900">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.billed_monthly)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.billed_backlinks)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.billed_website)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.billed_other)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-blue-700 bg-blue-50/50">{fmt(r.total_billed)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/50">{fmt(r.total_paid)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700 bg-amber-50/50">{r.outstanding > 0 ? fmt(r.outstanding) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-600">{fmt(r.consultant_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-600">{fmt(r.backlinks_cost)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold bg-neutral-50 ${r.margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(r.margin)}</td>
                  </tr>
                ))}
              </tbody>
              {summary?.rows?.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-neutral-300 bg-neutral-100 font-bold">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.billed_monthly)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.billed_backlinks)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.billed_website)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.totals.billed_other)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{fmt(summary.totals.total_billed)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmt(summary.totals.total_paid)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{fmt(summary.totals.outstanding)}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700">{fmt(summary.totals.consultant_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700">{fmt(summary.totals.backlinks_cost)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${summary.totals.margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(summary.totals.margin)}</td>
                  </tr>
                  <tr className="bg-rose-50 text-rose-700">
                    <td colSpan={10} className="px-3 py-2 text-right font-medium">− Frais généraux période</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.expenses.overhead)}</td>
                  </tr>
                  <tr className="bg-rose-50 text-rose-700">
                    <td colSpan={10} className="px-3 py-2 text-right font-medium">− Frais marketing période</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(summary.expenses.marketing)}</td>
                  </tr>
                  {summary.expenses.consultant_other > 0 && (
                    <tr className="bg-rose-50 text-rose-700">
                      <td colSpan={10} className="px-3 py-2 text-right font-medium">− Autres frais consulting</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(summary.expenses.consultant_other)}</td>
                    </tr>
                  )}
                  <tr className="bg-neutral-900 text-white text-base">
                    <td colSpan={10} className="px-3 py-3 text-right font-heading font-bold">MARGE NETTE ENTREPRISE</td>
                    <td className={`px-3 py-3 text-right font-mono font-bold ${summary.net_margin >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="finance-net-margin">
                      {fmt(summary.net_margin)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Charts */}
        <FinanceCharts timeseries={timeseries} />

        {/* Clients config */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="finance-clients-config">
          <h2 className="font-heading font-bold text-lg text-neutral-900 mb-3">Configuration financière des clients</h2>
          <p className="text-xs text-neutral-500 mb-3">Marquez les clients payants et renseignez les forfaits récurrents. Ces données sont strictement privées (jamais affichées dans le SaaS public).</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500 uppercase">
                <tr><th className="text-left py-1">Client</th><th>Payant</th><th className="text-right">Forfait/mois</th><th className="text-right">Backlinks/mois</th><th className="text-right">Consultant/mois</th><th></th></tr>
              </thead>
              <tbody>
                {clientsConfig.map(c => (
                  <tr key={c.client_id} className="border-t border-neutral-100">
                    <td className="py-2">{c.name}</td>
                    <td className="text-center">{c.is_paying ? '✅' : <span className="text-neutral-300">—</span>}</td>
                    <td className="text-right font-mono">{fmt(c.monthly_fee)}</td>
                    <td className="text-right font-mono">{fmt(c.monthly_backlinks_fee)}</td>
                    <td className="text-right font-mono">{fmt(c.monthly_consultant_fee)}</td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setConfigDialog(c)} data-testid={`config-${c.client_id}`}>
                        <Settings className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoices list */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="finance-invoices">
          <h2 className="font-heading font-bold text-lg text-neutral-900 mb-3">Factures de la période ({invoices.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500 uppercase">
                <tr>
                  <th className="text-left">Mois</th><th className="text-left">Client</th><th>Type</th><th>Libellé</th>
                  <th className="text-right">Montant</th><th>Statut</th><th className="text-left">CRM</th><th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => {
                  const client = clientsConfig.find(c => c.client_id === i.client_id);
                  return (
                    <tr key={i.id} className="border-t border-neutral-100">
                      <td className="py-2">{i.month}</td>
                      <td>{client?.name || i.client_id}</td>
                      <td><span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{i.type}</span></td>
                      <td className="text-neutral-600">{i.label}</td>
                      <td className="text-right font-mono">{fmt(i.amount)}</td>
                      <td><StatusBadge status={i.status} /></td>
                      <td>
                        {i.crm_target ? (
                          <span className="text-xs text-emerald-700">{i.crm_target} · {i.crm_external_ref?.slice(0, 12)}</span>
                        ) : (
                          <div className="flex gap-1">
                            <button onClick={() => handleSendToCRM(i.id, 'sellsy')} className="text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100" data-testid={`crm-sellsy-${i.id}`}>→ Sellsy</button>
                            <button onClick={() => handleSendToCRM(i.id, 'custom')} className="text-[11px] px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 hover:bg-cyan-100" data-testid={`crm-custom-${i.id}`}>→ Webhook</button>
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteInvoice(i.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {invoices.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-neutral-400">Aucune facture sur la période</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payments list */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="finance-payments">
          <h2 className="font-heading font-bold text-lg text-neutral-900 mb-3 flex items-center gap-2">
            <CalIcon className="w-5 h-5 text-emerald-600" /> Rentrées bancaires constatées ({payments.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500 uppercase">
                <tr><th className="text-left">Date</th><th className="text-left">Client</th><th>Méthode</th><th className="text-right">Montant</th><th>Notes</th><th></th></tr>
              </thead>
              <tbody>
                {payments.map(p => {
                  const client = clientsConfig.find(c => c.client_id === p.client_id);
                  return (
                    <tr key={p.id} className="border-t border-neutral-100">
                      <td className="py-2 font-mono">{p.payment_date}</td>
                      <td>{client?.name || p.client_id}</td>
                      <td><span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{p.method}</span></td>
                      <td className="text-right font-mono font-semibold text-emerald-700">{fmt(p.amount)}</td>
                      <td className="text-neutral-500 text-xs">{p.notes}</td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(p.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {payments.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-neutral-400">Aucune rentrée enregistrée sur la période</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {configDialog && <FinanceConfigDialog client={configDialog} onClose={() => setConfigDialog(null)} onSaved={loadAll} />}
      {invoiceDialog && <FinanceInvoiceDialog clients={clientsConfig.filter(c => c.is_paying)} defaultMonth={end} onClose={() => setInvoiceDialog(null)} onSaved={loadAll} />}
      {paymentDialog && <FinancePaymentDialog clients={clientsConfig.filter(c => c.is_paying)} invoices={invoices} onClose={() => setPaymentDialog(null)} onSaved={loadAll} />}
      {expenseDialog && <FinanceExpenseDialog defaultMonth={end} onClose={() => setExpenseDialog(false)} onSaved={loadAll} />}
      {webhookDialog && <FinanceCRMWebhookDialog onClose={() => setWebhookDialog(false)} />}
    </Layout>
  );
}

const KPI = ({ icon: Icon, label, value, cls, highlight, testid }) => (
  <div className={`border rounded-lg p-3 ${cls} ${highlight ? 'ring-2 ring-offset-1 ring-emerald-300' : ''}`} data-testid={testid}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider opacity-70">
      <span>{label}</span><Icon className="w-4 h-4" />
    </div>
    <div className="font-heading font-bold text-2xl mt-1 font-mono">{fmt(value)}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    draft: { cls: 'bg-neutral-100 text-neutral-600', label: 'Brouillon' },
    sent: { cls: 'bg-blue-50 text-blue-700', label: 'Envoyée' },
    paid: { cls: 'bg-emerald-50 text-emerald-700', label: 'Payée' },
  };
  const m = map[status] || map.draft;
  return <span className={`text-xs px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
};
